/**
 * Локальные сметы договора (эталон мира B) — загрузка и просмотр.
 *
 * Смета приходит от Заказчика выгрузкой Гранд-Сметы: одна книга — несколько
 * листов, каждый лист — отдельная ЛСР. Загружается книга целиком; повторная
 * загрузка обновляет те же сметы по их номеру, а состав позиций заменяется —
 * смета приходит одним документом, и частичная правка сделала бы её
 * неотличимой от недогруженной.
 *
 * Зачем эти сметы: только в них есть расшифровка цены — зарплата рабочих,
 * эксплуатация машин, зарплата машинистов и трудозатраты. Без них не заполнить
 * графы 7-16 КС-2, не собрать М-29 и не посчитать фонд оплаты труда.
 */
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useAgentPermission } from '@entities/user';
import { Button } from '@shared/ui/Button';
import { Input, Label, Field } from '@shared/ui/Input';
import {
  apiErrorText,
  useDeleteContractLsrMutation,
  useGetSmetaInfoQuery,
  useImportContractLsrMutation,
  useImportContractVersionEstimateMutation,
  useListContractLsrItemsQuery,
  useListContractLsrQuery,
  useListContractsQuery,
  useSyncContractLsrKspLaborMutation,
  useSyncContractLsrMaterialsMutation,
} from '@shared/api';
import { getActiveContractId, setActiveContractId } from '@shared/lib/activeProjectContext';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const Intro = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  line-height: 1.5;
`;
const Bar = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
  flex-wrap: wrap;
`;
const Wide = styled(Field)`
  flex: 1 1 380px;
`;
const ContractSelect = styled.select`
  height: 38px;
  width: 100%;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;
  th,
  td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    padding: 7px 9px;
    text-align: left;
    vertical-align: top;
  }
  th {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-weight: 600;
    white-space: nowrap;
  }
  td.num {
    text-align: right;
    white-space: nowrap;
  }
`;
const Row = styled.tr<{ $active: boolean }>`
  cursor: pointer;
  background: ${({ $active, theme }) => ($active ? theme.colors.surface : 'transparent')};
`;
const Note = styled.div`
  padding: 10px 12px;
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 13px;
  line-height: 1.5;
`;
const Err = styled(Note)`
  border-left-color: ${({ theme }) => theme.colors.status.danger};
`;
const Muted = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
`;

function num(v: number | null | undefined, digits = 2): string {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString('ru-RU', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export function ContractLsr() {
  const { can_write } = useAgentPermission('smeta');
  const [search, setSearch] = useState('');
  const [contractId, setContractId] = useState(() => getActiveContractId());
  const [openLsr, setOpenLsr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const infoQ = useGetSmetaInfoQuery();
  const lsrEnabled = infoQ.data?.flags?.lsr_import ?? false;
  const flagsKnown = !infoQ.isLoading && !infoQ.isError;

  const contractsQ = useListContractsQuery({ search: search.trim() || undefined, limit: 20 });
  const lsrQ = useListContractLsrQuery(contractId, { skip: !contractId });
  const itemsQ = useListContractLsrItemsQuery(
    { contractId, lsrId: openLsr ?? '' },
    { skip: !contractId || !openLsr }
  );
  const [importLsr, { isLoading: importing }] = useImportContractLsrMutation();
  const [removeLsr] = useDeleteContractLsrMutation();
  const [syncKspLabor, { isLoading: syncingKsp }] = useSyncContractLsrKspLaborMutation();
  const [importVersionEst, { isLoading: importingVersionEst }] =
    useImportContractVersionEstimateMutation();
  const [syncMaterials, { isLoading: syncingMaterials }] =
    useSyncContractLsrMaterialsMutation();

  const contracts = contractsQ.data?.items ?? [];
  const lsrs = useMemo(() => lsrQ.data?.items ?? [], [lsrQ.data]);
  const items = itemsQ.data?.items ?? [];

  async function upload(file: File) {
    setError(null);
    setResult(null);
    const form = new FormData();
    form.append('file', file);
    try {
      const stats = await importLsr({ contractId, form }).unwrap();
      const tail = stats.skipped.length ? ` Пропущено: ${stats.skipped.join('; ')}` : '';
      setResult(
        `Загружено смет: ${stats.lsr_count}, позиций: ${stats.position_count}.${tail}`
      );
    } catch (e) {
      setError(apiErrorText(e, 'Не удалось загрузить книгу смет'));
    }
  }

  /** Удаление сметы: детализация закрывается только после ответа сервера, отказ
   *  виден — раньше промис мутации без `unwrap()` проглатывал ошибку. */
  async function handleRemove(lsrId: string, code: string) {
    setError(null);
    setResult(null);
    try {
      await removeLsr({ contractId, lsrId }).unwrap();
      if (openLsr === lsrId) setOpenLsr(null);
    } catch (e) {
      setError(apiErrorText(e, `Не удалось удалить смету ${code}`));
    }
  }

  async function handleImportVersionEstimate() {
    if (!contractId) return;
    setError(null);
    setResult(null);
    try {
      const res = await importVersionEst({ contractId, target: 'both' }).unwrap();
      setResult(
        `Смета из комплекта договора (${res.filename}) импортирована: ЛСР — ${res.imported_lsr_count}, позиций — ${res.imported_lsr_items || res.estimate_items_count}.`
      );
    } catch (e) {
      setError(apiErrorText(e, 'Не удалось импортировать смету из версии договора'));
    }
  }

  async function handleSyncMaterials() {
    if (!contractId) return;
    setError(null);
    setResult(null);
    try {
      const res = await syncMaterials({ contractId }).unwrap();
      setResult(
        `Синхронизация контуров завершена: норм М-29 пополнено — ${res.synced_norms}, позиций потребности МТО — ${res.synced_mto_positions} (мост Ф3↔ЛСР: ${res.bridged_to_estimate}/${res.bridged_to_lsr}).`
      );
    } catch (e) {
      setError(apiErrorText(e, 'Не удалось синхронизировать материалы с М-29 и МТО'));
    }
  }

  async function handleSyncKspLabor() {
    if (!contractId) return;
    setError(null);
    setResult(null);
    try {
      const res = await syncKspLabor({ contractId }).unwrap();
      setResult(
        `Нормативная трудоёмкость передана в КСП: обновлено работ в графике — ${res.synced_activities} (из ${res.total_activities} работ графика по ${res.total_lsr_items} позициям ЛСР).`
      );
    } catch (e) {
      setError(apiErrorText(e, 'Не удалось синхронизировать трудоёмкость с КСП'));
    }
  }

  return (
    <Wrap>
      <Intro>
        Локальные сметы Заказчика — эталон, против которого собираются акты. Загружается книга
        целиком: каждый лист выгрузки Гранд-Сметы становится отдельной сметой. Именно отсюда берутся
        зарплата рабочих, эксплуатация машин и трудозатраты — без них не заполнить графы 7-16 КС-2,
        М-29 и расчёт фонда оплаты труда.
      </Intro>

      {flagsKnown && !lsrEnabled && (
        <Err>
          Загрузка локальных смет отключена на сервере. Администратору: добавьте{' '}
          <code>OSNOVA_LSR_IMPORT_ENABLED=1</code> в <code>/opt/osnova/.env</code> и перезапустите
          backend.
        </Err>
      )}

      <Bar>
        <Wide>
          <Label htmlFor="lsr-contract-search">Договор</Label>
          <Input
            id="lsr-contract-search"
            placeholder="Поиск по номеру, контрагенту или предмету"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Wide>
        <Wide>
          <Label htmlFor="lsr-contract">Выбранный договор</Label>
          <ContractSelect
            id="lsr-contract"
            value={contractId}
            onChange={(e) => {
              const nextId = e.target.value;
              setContractId(nextId);
              setActiveContractId(nextId);
              setOpenLsr(null);
              setResult(null);
              setError(null);
            }}
          >
            <option value="">— не выбран —</option>
            {contracts.map((c) => (
              <option key={c.id} value={c.id}>
                {[c.contract_number, c.counterparty, c.subject].filter(Boolean).join(' · ')}
              </option>
            ))}
          </ContractSelect>
        </Wide>
        {can_write && contractId && (
          <Field>
            <Label htmlFor="lsr-file">Книга смет (.xlsx)</Label>
            <input
              id="lsr-file"
              type="file"
              accept=".xlsx"
              disabled={importing}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void upload(f);
                e.target.value = '';
              }}
            />
          </Field>
        )}
        {can_write && contractId && (
          <Button
            type="button"
            $variant="secondary"
            $size="sm"
            disabled={importingVersionEst}
            onClick={handleImportVersionEstimate}
            title="Подтянуть сметное приложение из последней версии договора в Статуте"
          >
            {importingVersionEst ? 'Импорт из договора…' : 'Подтянуть смету из договора'}
          </Button>
        )}
        {can_write && contractId && (
          <Button
            type="button"
            $variant="secondary"
            $size="sm"
            disabled={syncingMaterials}
            onClick={handleSyncMaterials}
            title="Синхронизировать эталоны Ф3↔ЛСР и выгрузить материалы в нормы М-29 и потребность МТО"
          >
            {syncingMaterials ? 'Синхронизация…' : 'Синхронизировать с М-29 и МТО'}
          </Button>
        )}
        {can_write && contractId && lsrs.length > 0 && (
          <Button
            type="button"
            $variant="secondary"
            $size="sm"
            disabled={syncingKsp}
            onClick={handleSyncKspLabor}
            title="Передать нормативные чел.-ч из локальных смет в работы КСП"
          >
            {syncingKsp ? 'Синхронизация…' : 'Передать трудоёмкость в КСП'}
          </Button>
        )}
      </Bar>

      {importing && <Note>Разбираем книгу — на больших сметах это занимает до минуты.</Note>}
      {result && <Note>{result}</Note>}
      {error && <Err>{error}</Err>}

      {contractId && (
        <Table>
          <thead>
            <tr>
              <th>Номер сметы</th>
              <th>Наименование работ</th>
              <th>База цен</th>
              {can_write && <th />}
            </tr>
          </thead>
          <tbody>
            {lsrs.map((l) => (
              <Row
                key={l.id}
                $active={l.id === openLsr}
                onClick={() => setOpenLsr(l.id === openLsr ? null : l.id)}
              >
                <td>{l.code}</td>
                <td>{l.name}</td>
                <td>
                  {l.pricing_base === 'base2001_indexed' ? (
                    'базисные цены + индексы'
                  ) : (
                    <Muted>{l.pricing_base ?? '—'}</Muted>
                  )}
                </td>
                {can_write && (
                  <td>
                    <Button
                      type="button"
                      $variant="ghost"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        if (window.confirm(`Удалить смету ${l.code} вместе с позициями?`)) {
                          void handleRemove(l.id, l.code);
                        }
                      }}
                    >
                      Удалить
                    </Button>
                  </td>
                )}
              </Row>
            ))}
            {lsrs.length === 0 && !lsrQ.isLoading && (
              <tr>
                <td colSpan={can_write ? 4 : 3}>
                  По договору не загружено ни одной сметы.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      {openLsr && (
        <>
          <Intro>
            Позиции сметы. «Всего» — прямые затраты за единицу; следом её составляющие, по которым
            и заполняются графы КС-2.
          </Intro>
          <Table>
            <thead>
              <tr>
                <th>Поз.</th>
                <th>Шифр</th>
                <th>Наименование</th>
                <th>Ед.</th>
                <th>Кол-во</th>
                <th>Всего</th>
                <th>Зарплата</th>
                <th>Машины</th>
                <th>З/п машинистов</th>
                <th>Т/з раб.</th>
                <th>Т/з мех.</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td>{it.pos_no}</td>
                  <td>{it.code ?? <Muted>по цене поставки</Muted>}</td>
                  <td>{it.name.split('\n')[0]}</td>
                  <td>{it.unit ?? '—'}</td>
                  <td className="num">{num(it.qty_contract, 3)}</td>
                  <td className="num">{num(it.unit_price)}</td>
                  <td className="num">{num(it.unit_ozp)}</td>
                  <td className="num">{num(it.unit_emm)}</td>
                  <td className="num">{num(it.unit_zpm)}</td>
                  <td className="num">{num(it.labor_workers, 2)}</td>
                  <td className="num">{num(it.labor_machinists, 2)}</td>
                </tr>
              ))}
              {items.length === 0 && !itemsQ.isLoading && (
                <tr>
                  <td colSpan={11}>В смете нет позиций.</td>
                </tr>
              )}
            </tbody>
          </Table>
        </>
      )}
    </Wrap>
  );
}
